import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {SupplierAddEditDialog} from "./SupplierAddEditDialog";
import {ActionsColumnFormatter} from "../../column-formatters/ActionsColumnFormatter";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {withSyncDot} from "../../column-formatters/SyncStatusColumnFormatter";
import {ENTITY_ID_SUPPLIER} from "../generic/Constants";
import {SyncProgressDialog} from "../../generic-components/SyncProgressDialog";
import {useSyncOperations} from "../../hooks/useSyncOperations";
import {useSyncEnabled} from "../../hooks/useSyncEnabled";

export function SuppliersPage() {
    const myData = useContext(BedRockContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState(null);

    // Check if entity is sync-enabled and get push/pull capabilities
    const {syncEnabled, pushEnabled, pullEnabled} = useSyncEnabled(ENTITY_ID_SUPPLIER);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    // Use sync operations hook with push/pull capabilities
    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        pullFromServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_SUPPLIER, reloadData, { pushEnabled, pullEnabled });

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Build columns dynamically based on sync enabled status
    const columns = useMemo(() => {
        const baseColumns = [
            {
                dataField: "name",
                text: "Supplier Name",
                search: true,
                formatter: syncEnabled ? withSyncDot(HeaderColumnFormatter) : HeaderColumnFormatter
            },
            {
                dataField: "billingName",
                text: "Billing Name",
            },
            {
                dataField: "phone",
                text: "Phone",
            },
            {
                dataField: "gstNumber",
                text: "GST Number",
            },
            {
                dataField: "pinCode",
                text: "Pin Code",
            },
        ];

        // Add actions column
        baseColumns.push({
            dataField: "action",
            text: "Actions",
            headerStyle: {
                width: '100px',
            },
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons: [
                    {
                        name: "Edit",
                        color: "primary",
                        icon: "edit",
                        method: openEditDialog
                    }
                ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
            style: {
                minWidth: "100px",
            },
        });

        return baseColumns;
    }, [syncEnabled]);

    function openEditDialog(row) {
        setShowModelId(row.id);
        setShowModal(true);
    }

    function addButtonClicked() {
        setShowModelId(null);
        setShowModal(true);
    }

    function handleClose() {
        setShowModal(false);
        reloadData();
    }

    useEffect(() => {
        if (myData && myData.setBaseData) {
            // Build toolbar buttons based on sync capabilities
            const toolBarButtons = [];

            if (syncEnabled && pushEnabled) {
                toolBarButtons.push({
                    name: "Push to Server",
                    color: "info",
                    icon: "fa fa-cloud-upload-alt",
                    onClick: pushToServer,
                });
            }

            if (syncEnabled && pullEnabled) {
                toolBarButtons.push({
                    name: "Pull from Server",
                    color: "success",
                    icon: "fa fa-cloud-download-alt",
                    onClick: pullFromServer,
                });
            }

            toolBarButtons.push({
                name: "ADD SUPPLIER",
                color: "primary",
                icon: "fa fa-plus",
                onClick: addButtonClicked,
            });

            myData.setBaseData({toolBarButtons});
        }

        setPageLoadComplete(true);
    }, [pushToServer, pullFromServer, syncEnabled, pushEnabled, pullEnabled]);

    // Handle action=create query parameter
    useEffect(() => {
        if (searchParams.get("action") === "create") {
            setShowModelId(null);
            setShowModal(true);
            // Clear the query parameter to prevent reopening on navigation
            searchParams.delete("action");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const BaseCard = () => (
        <div className="card card-body">
            {pageLoadComplete && (
                <>
                    <GenericTable
                        reload={reload}
                        defaultData={defaultList}
                        columns={columns}
                        entityId={ENTITY_ID_SUPPLIER}
                    />
                </>
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Suppliers</PageTitle>
            <BaseCard/>
            <SupplierAddEditDialog show={showModal} handleClose={handleClose} id={showModelId}/>
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
        </>
    );
}
